import { GlobalData, ParamObj, ProcessData, SCANNER_TYPES, ScannerType, STEP_ACTION_TYPE, StepEventData } from '@mirage-projects/cw-models';

/* Define processes here */
const instrumentHeight: ProcessData = {
    name: 'instrumentHeight',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET INSTRUMENT_HEIGHT PROCESS_STARTED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: { type: 'GET INSTRUMENT_HEIGHT PROCESS_PROGRESS', message: { progress: 10 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: { type: 'GET INSTRUMENT_HEIGHT PROCESS_PROGRESS', message: { progress: 20 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: { type: 'GET INSTRUMENT_HEIGHT PROCESS_PROGRESS', message: { progress: 60 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: { type: 'GET INSTRUMENT_HEIGHT PROCESS_PROGRESS', message: { progress: 80 } },
        },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 2000, event: { type: 'GET INSTRUMENT_HEIGHT PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: { type: 'GET INSTRUMENT_HEIGHT RESPONSE', message: { success: true, height: 1.32 } },
        },
    ],
};

const instrumentHeightFail: ProcessData = {
    name: 'instrumentHeightFail',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET INSTRUMENT_HEIGHT PROCESS_STARTED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: { type: 'GET INSTRUMENT_HEIGHT PROCESS_PROGRESS', message: { progress: 50 } },
        },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 2000, event: { type: 'GET INSTRUMENT_HEIGHT PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: { type: 'GET INSTRUMENT_HEIGHT RESPONSE', message: { success: false, height: 1.32 } },
        },
    ],
};

const stationTransformations: ProcessData = {
    name: 'stationTransformations',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET STATIONS_TRANSFORMATIONS REQUEST',
                message: { stationUuidList: globalData?.stationUuidList || [] },
            }),
        },
        { action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET STATIONS_TRANSFORMATIONS RESPONSE' },
    ],
};

const startReportPickPoint: ProcessData = {
    name: 'startReportPickPoint',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET REPORT_PICK_POINT REQUEST',
                message: { enabled: true, reportPointFeatures: true },
            }),
        },
        { action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET REPORT_PICK_POINT RESPONSE' },
    ],
};

const stopReportPickPoint: ProcessData = {
    name: 'stopReportPickPoint',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET REPORT_PICK_POINT REQUEST',
                message: { enabled: false },
            }),
        },
        { action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET REPORT_PICK_POINT RESPONSE' },
    ],
};

const stationPosition: ProcessData = {
    name: 'stationPosition',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, event: { type: 'GET STATION_POSITION REQUEST' } },
        { action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET STATION_POSITION RESPONSE' },
    ],
};

const requestUserPreferences: ProcessData = {
    name: 'requestUserPreferences',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET USER_PREFERENCES REQUEST', message: { queryList: ['DISTANCE_UNITS', 'AREA_UNITS', 'NUMBER_OF_DECIMALS'] } },
        },
        { action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET USER_PREFERENCES RESPONSE' },
    ],
};

const requestBounding: ProcessData = {
    name: 'requestBounding',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET PROJECT_DATA REQUEST', message: { queryList: ['DATA_BOUNDING'] } },
        },
        { action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET PROJECT_DATA RESPONSE' },
    ],
};

const requestPivotPoint: ProcessData = {
    name: 'requestPivotPoint',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET PROJECT_DATA REQUEST', message: { queryList: ['PIVOT_POINT'] } },
        },
        { action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET PROJECT_DATA RESPONSE' },
    ],
};

const requestProjectName: ProcessData = {
    name: 'requestProjectName',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET PROJECT_DATA REQUEST', message: { queryList: ['PROJECT_NAME'] } },
        },
        { action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET PROJECT_DATA RESPONSE' },
    ],
};

const requestProjectData: ProcessData = {
    name: 'requestProjectData',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET PROJECT_DATA REQUEST',
                message: { queryList: globalData?.queryList ?? ['PROJECT_NAME', 'PROJECT_GUID', 'PIVOT_POINT'] },
            }),
        },
        { action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET PROJECT_DATA RESPONSE' },
    ],
};

const notifyWorkflowState: ProcessData = {
    name: 'notifyWorkflowState',
    steps: [{ action: STEP_ACTION_TYPE.SEND, event: { type: 'SET WORKFLOW_STATE RESPONSE', message: { state: 'RegistrationOccBs' } } }],
};

const waitForNotifyWorkflowState: ProcessData = {
    name: 'waitForNotifyWorkflowState',
    steps: [{ action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'SET WORKFLOW_STATE RESPONSE' }],
};

const notifyScannerState: ProcessData = {
    name: 'notifyScannerState',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'SET SCANNER_STATE RESPONSE',
                message: { success: true, state: globalData?.state || 'CONNECTED', type: globalData?.scannerType || 'GLS' },
            }),
        },
    ],
};

const notifyScannerStateFail: ProcessData = {
    name: 'notifyScannerStateFail',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'SET SCANNER_STATE RESPONSE',
                message: { success: false, state: globalData?.state || 'CONNECTED', type: globalData?.scannerType || 'GLS' },
            }),
        },
    ],
};

// You colud write :
// http://localhost:8080/scan-viewer/29?mocksenabled_Raster or mocksenabledRaster
// http://localhost:8080/scan-viewer/29?mocksenabled_GTL or mocksenabledGTL
// http://localhost:8080/scan-viewer/29?mocksenabled_GT or mocksenabledGT
// http://localhost:8080/scan-viewer/29?mocksenabled_GLS or mocksenabledGLS
//
// to enable mocks and set a specific device (simple add teh word of device)
//
// When use the mocksenabled 'url trick',
// by default, 'GLS' is selected,  using or not the '_####' url option

const _getScannerType = (globalDataScannerType?: ScannerType): ScannerType => {
    const url = window.location.href;
    const areMocksEnabled = url.toLowerCase().includes('mocksenabled') && !globalDataScannerType;
    const scannerType: ScannerType = areMocksEnabled
        ? (SCANNER_TYPES.find((type) => url.includes(type)) ?? 'GLS')
        : (globalDataScannerType ?? 'GLS'); // default

    return scannerType;
};

const getScannerState: ProcessData = {
    name: 'getScannerState',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET SCANNER_STATE RESPONSE',
                message: { success: true, state: globalData?.state ?? 'CONNECTED', type: _getScannerType(globalData?.scannerType) },
            }),
        },
    ],
};

const createNewStation: ProcessData = {
    name: 'createNewStation',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'CREATE NEW_STATION PROCESS_STARTED' } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 3000, event: { type: 'CREATE NEW_STATION PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: {
                type: 'CREATE NEW_STATION RESPONSE',
                message: {
                    success: true,
                    stationUuid: '0600_0000_0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                    projectUuid: '0600_0000_1111aaaa-0000-aaaa-0000-aaaa0000aaaa',
                    stationName: 'testStation',
                },
            },
        },
    ],
};

const createNewStationManually: ProcessData = {
    name: 'createNewStationManually',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'CREATE NEW_STATION PROCESS_STARTED' } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 3000, event: { type: 'CREATE NEW_STATION PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'CREATE NEW_STATION RESPONSE',
                message: {
                    success: true,
                    stationUuid: globalData?.stationUuid || '0600_0000_0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                    projectUuid: '0600_0000_1111aaaa-0000-aaaa-0000-aaaa0000aaaa',
                    stationName: globalData?.stationName || 'testStation',
                },
            }),
        },
    ],
};

const createNewStationFail: ProcessData = {
    name: 'createNewStationFail',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'CREATE NEW_STATION PROCESS_STARTED' } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 3000, event: { type: 'CREATE NEW_STATION PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: {
                type: 'CREATE NEW_STATION RESPONSE',
                message: { success: false, stationUuid: '0000aaaa-0000-aaaa-0000-aaaa0000aaaa' },
            },
        },
    ],
};

// Create,if you want a 'simTargets.csv' file with this control points
// Import that file as references.
// Points are the same as the mock responses for getstation & getstation2
// T1,-5.000,10.000,20.000
// T2, 0.000,10.000,20.000
// T3, 5.000,10.000,20.000
// T4, 0.000, 5.000,20.000

const sendScanTarget: ProcessData = {
    name: 'sendScanTarget',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET SCAN_TARGET RESPONSE',
                message: {
                    success: true,
                    method: 'RESECTION',
                    stationUuid: '0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                    target: globalData?.target ?? {
                        name: 'TargetMock1',
                        coordinates: [-5, 10, 20],
                        type: 'type1',
                        height: 1.2,
                        prismCorrection: 0.2,
                    },
                    isCorrect: globalData?.isCorrect !== undefined ? globalData?.isCorrect : undefined,
                },
            }),
        },
    ],
};

const sendScanTarget2: ProcessData = {
    name: 'sendScanTarget2',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET SCAN_TARGET2 RESPONSE',
                message: {
                    success: true,
                    method: 'RESECTION',
                    stationUuid: '0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                    target: globalData?.target || {
                        name: 'TargetMock2',
                        coordinates: [5, 10, 20],
                        type: 'type1',
                        height: 1.2,
                        prismCorrection: 0.2,
                    },
                },
            }),
        },
    ],
};

const sendScanTargetFail: ProcessData = {
    name: 'sendScanTargetFail',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET SCAN_TARGET RESPONSE',
                message: {
                    success: false,
                    method: 'RESECTION',
                    stationUuid: '0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                    target: globalData?.target || {
                        name: 'TargetMock1',
                        coordinates: [-5, 10, 20],
                        type: 'type1',
                        height: 1.2,
                        prismCorrection: 0.2,
                    },
                },
            }),
        },
    ],
};
const waitForTransferStationRequest: ProcessData = {
    name: 'waitForTransferStationRequest',
    steps: [{ action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET TRANSFER_STATION_DATA REQUEST' }],
};

const setCurrentStation: ProcessData = {
    name: 'setCurrentStation',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'SET CURRENT_STATION RESPONSE',
                message: {
                    success: true,
                    stationUuid: globalData?.stationUuid !== undefined ? globalData?.stationUuid : '0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                },
            }),
        },
    ],
};

const setCurrentStationFail: ProcessData = {
    name: 'setCurrentStationFail',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'SET CURRENT_STATION RESPONSE',
                message: {
                    success: false,
                    stationUuid: globalData?.stationUuid !== undefined ? globalData?.stationUuid : '0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                },
            }),
        },
    ],
};

const getScanCloud: ProcessData = {
    name: 'getScanCloud',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_CLOUD PROCESS_STARTED' } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_CLOUD PROCESS_PROGRESS', message: { progress: 20 } } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_CLOUD PROCESS_PROGRESS', message: { progress: 60 } } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_CLOUD PROCESS_PROGRESS', message: { progress: 80 } } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_CLOUD PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET SCAN_CLOUD RESPONSE',
                message: {
                    success: true,
                    stationUuid: globalData?.stationUuid || '0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                    /*
                            finished: true --> To simulate Review Scan(s) toggle ON
                            finished: false --> To simulate Review Scan(s) toggle OFF
                        */
                    finished: true,
                },
            }),
        },
    ],
};

const getWindowScanCloud: ProcessData = {
    name: 'getWindowScanCloud',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET WINDOW_SCAN_CLOUD PROCESS_STARTED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET WINDOW_SCAN_CLOUD PROCESS_PROGRESS', message: { progress: 20 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET WINDOW_SCAN_CLOUD PROCESS_PROGRESS', message: { progress: 60 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET WINDOW_SCAN_CLOUD PROCESS_PROGRESS', message: { progress: 80 } },
        },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET WINDOW_SCAN_CLOUD PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET SCAN_CLOUD RESPONSE',
                message: { success: true, stationUuid: globalData?.stationUuid || '0000aaaa-0000-aaaa-0000-aaaa0000aaaa' },
            }),
        },
    ],
};

const getScanCloudFail: ProcessData = {
    name: 'getScanCloudFail',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_CLOUD PROCESS_STARTED' } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_CLOUD PROCESS_PROGRESS', message: { progress: 50 } } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_CLOUD PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET SCAN_CLOUD RESPONSE',
                message: { success: false, stationUuid: globalData?.stationUuid || '0000aaaa-0000-aaaa-0000-aaaa0000aaaa' },
            }),
        },
    ],
};

const getScanTarget: ProcessData = {
    name: 'getScanTarget',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_TARGET PROCESS_STARTED' } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_TARGET PROCESS_PROGRESS', message: { progress: 20 } } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_TARGET PROCESS_PROGRESS', message: { progress: 60 } } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_TARGET PROCESS_PROGRESS', message: { progress: 80 } } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_TARGET PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET SCAN_TARGET RESPONSE',
                message: {
                    success: true,
                    stationUuid: globalData?.stationUuid || '0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                    target: globalData?.target || {
                        name: 'TargetMock1',
                        coordinates: [-5, 10, 20],
                        type: 'type1',
                        height: 1.2,
                        prismCorrection: 0.2,
                    },
                },
            }),
        },
    ],
};

const getScanTarget2: ProcessData = {
    name: 'getScanTarget2',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_TARGET PROCESS_STARTED' } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_TARGET PROCESS_PROGRESS', message: { progress: 20 } } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_TARGET PROCESS_PROGRESS', message: { progress: 60 } } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_TARGET PROCESS_PROGRESS', message: { progress: 80 } } },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_TARGET PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET SCAN_TARGET2 RESPONSE',
                message: {
                    success: true,
                    stationUuid: globalData?.stationUuid || '0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                    target: globalData?.target || {
                        name: 'TargetMock2',
                        coordinates: [5, 10, 20],
                        type: 'type1',
                        height: 1.2,
                        prismCorrection: 0.2,
                    },
                },
            }),
        },
    ],
};

const getScanCloudAndResection: ProcessData = {
    name: 'getScanCloudAndResection',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_CLOUD_AND_RESECTION PROCESS_STARTED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET SCAN_CLOUD_AND_RESECTION PROCESS_PROGRESS', message: { progress: 20 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET SCAN_CLOUD_AND_RESECTION PROCESS_PROGRESS', message: { progress: 60 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET SCAN_CLOUD_AND_RESECTION PROCESS_PROGRESS', message: { progress: 80 } },
        },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET SCAN_CLOUD_AND_RESECTION PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET SCAN_CLOUD_AND_RESECTION RESPONSE',
                message: {
                    success: true,
                    stationUuid: globalData?.stationUuid ?? '0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                    resectionPairs: [
                        {
                            referencePoint: { name: params.resectionPairs?.[0].referencePoint.name ?? 'T1' },
                            target: {
                                name: 'TAR0001',
                                coordinates: [-5, 10, 20],
                                height: params.resectionPairs?.[0].target.height ?? 1.2,
                                type: '360Prism',
                                prismCorrection: -7,
                            },
                            isCorrect: true,
                        },
                        {
                            referencePoint: { name: params.resectionPairs?.[1].referencePoint.name ?? 'T3' },
                            target: {
                                name: 'TAR0002',
                                coordinates: [5, 10, 20],
                                height: params.resectionPairs?.[1].target.height ?? 1.34,
                                type: '360Prism',
                                prismCorrection: -7,
                            }, // change to undefined to force error
                            isCorrect: true,
                        },
                    ],
                    pairsMatched: false, // change to true to launch only resection
                },
            }),
        },
    ],
};

// For testing event without measurements or with just 1 measure
// resectionPairs: [ { referencePoint: { 'name': params.resectionPairs[ 0 ].referencePoint.name || 'T1' } /*, target: { name: 'TAR0001', coordinates: [ -5, 10, 20 ], type: 'type1', height: params.resectionPairs[ 0 ].target.height || 1.2, prismCorrection: 0.2 }*/ }, { referencePoint: { 'name': params.resectionPairs[ 1 ].referencePoint.name || 'T3' } /*, target: { name: 'TAR0002', coordinates: [ 5, 10, 20 ], type: 'type1', height: params.resectionPairs[ 1 ].target.height || 1.34, prismCorrection: 0.4 }*/ } ],
// resectionPairs: [ { referencePoint: { 'name': params.resectionPairs[ 0 ].referencePoint.name || 'T1' }, target: { name: 'TAR0001', coordinates: [ -5, 10, 20 ], type: 'type1', height: params.resectionPairs[ 0 ].target.height || 1.2, prismCorrection: 0.2 } }, { referencePoint: { 'name': params.resectionPairs[ 1 ].referencePoint.name || 'T3' } /*, target: { name: 'TAR0002', coordinates: [ 5, 10, 20 ], type: 'type1', height: params.resectionPairs[ 1 ].target.height || 1.34, prismCorrection: 0.4 }*/ } ],

// resectionPairs when from 2 points, 3 targets found
// [ { referencePoint: { 'name': params.resectionPairs[ 0 ].referencePoint.name || 'W4' }, target: { name: 'TAR0001', coordinates: [ -0.591656, -1.890856, 0.862109 ], type: 'type1', height: params.resectionPairs[ 0 ].target.height || 0, prismCorrection: -7 } },
//   { referencePoint: { 'name': params.resectionPairs[ 1 ].referencePoint.name || 'W6' }, target: { name: 'TAR0002', coordinates: [ 6.238891, 5.399865, 0.685185 ], type: 'type1', height: params.resectionPairs[ 1 ].target.height || 0, prismCorrection: -7 } },
//   { referencePoint: { 'name': 'W4' }, target: { name: 'TAR0003', coordinates: [ -3.393347, 4.146267, 0.837566 ], type: 'type1', height: params.resectionPairs[ 1 ].target.height || 0, prismCorrection: -7 } }
// ],

// one target only height
//{ referencePoint: { 'name': params.resectionPairs[ 0 ].referencePoint.name || 'T1' }, target: { /*name: 'TAR0001', coordinates: [ -5, 10, 20 ], type: 'type1',*/ height: params.resectionPairs[ 0 ].target.height || 1.2 /*, prismCorrection: 0.2*/ } },

const getMultiScanCloud: ProcessData = {
    name: 'getMultiScanCloud',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET MULTI_SCAN_CLOUD PROCESS_STARTED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET MULTI_SCAN_CLOUD PROCESS_PROGRESS', message: { progress: 20 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: {
                type: 'GET MULTI_SCAN_CLOUD PROCESS_PROGRESS',
                message: {
                    progress: 60,
                    finishedScan: {
                        scanUuid: 'c7c2fe72-3333-0c36-3333-75678c1d1db3',
                        instanceGUID: '2fdc8d47-efe4-4f47-b6a4-69a0b1e37656',
                    },
                },
            },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET MULTI_SCAN_CLOUD PROCESS_PROGRESS', message: { progress: 80 } },
        },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET MULTI_SCAN_CLOUD PROCESS_FINISHED' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'GET MULTI_SCAN_CLOUD RESPONSE',
                message: { success: true, stationUuid: globalData?.stationUuid ?? '0000aaaa-0000-aaaa-0000-aaaa0000aaaa' },
            }),
        },
    ],
};

const getTransferStation: ProcessData = {
    name: 'getTransferStation',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 2000,
            event: {
                type: 'GET TRANSFER_STATION_DATA RESPONSE',
                message: { success: true, stationUuid: '0000aaaa-0000-aaaa-0000-aaaa0000aaaa' },
            },
        },
    ],
};

const getTransferStationFail: ProcessData = {
    name: 'getTransferStationFail',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 2000,
            event: {
                type: 'GET TRANSFER_STATION_DATA RESPONSE',
                message: { success: false, stationUuid: '0000aaaa-0000-aaaa-0000-aaaa0000aaaa' },
            },
        },
    ],
};

const waitForLayoutPointRequest: ProcessData = {
    name: 'waitForLayoutPointRequest',
    steps: [{ action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET LAYOUT_POINT REQUEST' }],
};

const waitForColumnSize: ProcessData = {
    name: 'waitForColumnSize',
    steps: [{ action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'SET RIGHT_COLUMN_SIZE RESPONSE' }],
};

const getStandardVerification: ProcessData = {
    name: 'getStandardVerification',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET STANDARD_VERIFICATION REQUEST' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET STANDARD_VERIFICATION PROCESS_STARTED', message: { progress: 20 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET STANDARD_VERIFICATION PROCESS_PROGRESS', message: { progress: 60 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET STANDARD_VERIFICATION PROCESS_FINISHED', message: { progress: 80 } },
        },
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET STANDARD_VERIFICATION RESPONSE' } },
    ],
};

const getTotalStationVerification: ProcessData = {
    name: 'getTotalStationVerification',
    steps: [
        { action: STEP_ACTION_TYPE.SEND, runDelay: 500, event: { type: 'GET TOTALSTATION_VERIFICATION REQUEST' } },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET TOTALSTATION_VERIFICATION PROCESS_STARTED', message: { progress: 20 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET TOTALSTATION_VERIFICATION PROCESS_PROGRESS', message: { progress: 60 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: { type: 'GET TOTALSTATION_VERIFICATION PROCESS_FINISHED', message: { progress: 80 } },
        },
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 500,
            event: {
                type: 'GET TOTALSTATION_VERIFICATION RESPONSE',
                message: {
                    success: true,
                    FileFormatID: '53',
                    DateTime: '2022-09-23T21:59:53.4279983Z',
                    UnitLength: 'Meter',
                    CommandGUID: '8347ac72-aa0f-453a-2bca978347-795ac7',
                    CommandName: 'VerityAnalyze',
                    InputDirectory: '/app/data/input',
                    OutputDirectory: '/app/data/output',
                    AnalysisInstanceCollection: [
                        {
                            InstanceGUID: '2486afbb-e48b-4934-8e19-e95799f73d02',
                            InstallationStatus: 'Installed',
                            Conformance: 'Out Of Tolerance',
                            ScanCoverage: 0.721946039866789,

                            AsBuiltTransform: {
                                Matrix: [
                                    0.99978305672809831, -0.020828813682505859, 0, 0, 0, 0, -1, 0, 0.020828813682505859,
                                    0.99978305672809831, 0, 0, 59.997055948321645, -31.178723302229898, 99.529899999435528, 1,
                                ],
                                IsIdentity: false,
                            },
                            HeatMapInformation: {
                                PTSFilePath: '3486afbb-e48b-4934-8e19-e95799f73d02.pts',
                                HeatMapFilePath: '3486afbb-e48b-4934-8e19-e95799f73d02.c3hmd',
                            },
                            Deviation: {
                                AsDesignedTranslation: 1.894563135,
                                AsDesignedVerticalRotation: 2.235725399663818,
                                AsDesignedHorizontalRotation: 4.121338110041549,
                                RotationFromHorizontal: 1.059427410918966,
                                HorizontalDisplacement: 2.866977068236735,
                                LongAxisDisplacement: 1.3670444240967994,
                                RotationFromVertical: 3.4792939743066476,
                                TwistRotation: 2.2358365215385154,
                                VerticalDisplacement: 4.840050771067053,
                                XDisplacement: 3.644678493467505,
                                YDisplacement: 0.7359262060930263,
                            },
                        },
                    ],
                },
            },
        },
    ],
};

const waitForTotalStationVerification: ProcessData = {
    name: 'waitForTotalStationVerification',
    steps: [{ action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'GET TOTALSTATION_VERIFICATION REQUEST' }],
};

const waitForNotifyNavigationView: ProcessData = {
    name: 'waitForNotifyNavigationView',
    steps: [{ action: STEP_ACTION_TYPE.RECEIVE, eventFilter: 'SET LAYOUT_NAVIGATION_VIEW RESPONSE' }],
};

const setPrismPolePosition: ProcessData = {
    name: 'setPrismPolePosition',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            event: (params: ParamObj, globalData?: GlobalData): StepEventData => ({
                type: 'SET PRISM_POLE_POSITION RESPONSE',
                message: { success: true, coordinates: globalData?.prismPos !== undefined ? globalData?.prismPos : [10, 10, 10] },
            }),
        },
    ],
};

const getFileContents: ProcessData = {
    name: 'getFileContents',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params, globalData) => ({
                type: 'ACTION GET_FILE_CONTENTS RESPONSE',
                message: {
                    content: globalData?.content ?? {
                        projects: globalData?.projects ?? [
                            {
                                id: 'df9c1e9a-89a3-4037-a235-3f9f44dff68c',
                                name: 'Example project 1',
                                scans: [
                                    {
                                        id: '488c1a55-3be2-43eb-91f4-b61a5a88c7ec',
                                        name: 'P1_Scan_001',
                                        captureDate: '2025-04-03T13:15:31+02:00',
                                        filesPath: 'Scans/P1_Scan_001.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                    {
                                        id: '02a50720-23c8-4c29-b497-1107cc15a438',
                                        name: 'P1_Scan_002',
                                        captureDate: '2025-04-03T13:18:31+02:00',
                                        filesPath: 'Scans/P1_Scan_002.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                    {
                                        id: 'c2b267c4-7a58-4d42-8e89-15a6cd10d518',
                                        name: 'P1_Scan_003',
                                        captureDate: '2025-04-03T13:16:31+02:00',
                                        filesPath: 'Scans/P1_Scan_003.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                    {
                                        id: '8ef7474f-36e3-4a6f-80e4-df84cfb7b45b',
                                        name: 'P1_Scan_004',
                                        captureDate: '2025-04-03T13:19:31+02:00',
                                        filesPath: 'Scans/P1_Scan_004.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                ],
                            },
                            {
                                id: '649b72de-f67d-4b8c-b5c3-7f3e9d648117',
                                name: 'Example project 2',
                                scans: [
                                    {
                                        id: '0b7a7ab3-37a0-4073-83ff-163a7aa4df23',
                                        name: 'P2_Scan_001',
                                        captureDate: '2025-04-03T14:16:31+02:00',
                                        filesPath: 'Scans/P2_Scan_001.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                    {
                                        id: 'a4bfa02b-3de7-4e2b-a5e3-7301e57f1f98',
                                        name: 'P2_Scan_002',
                                        captureDate: '2025-04-03T14:17:31+02:00',
                                        filesPath: 'Scans/P2_Scan_002.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                    {
                                        id: 'dbb3e3b0-5c8b-4721-90fa-4a5b26f46c12',
                                        name: 'P2_Scan_003',
                                        captureDate: '2025-04-03T14:18:31+02:00',
                                        filesPath: 'Scans/P2_Scan_003.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                ],
                            },
                            {
                                id: '32c8d8b0-1e7f-4fc9-8cbe-168c877d3029',
                                name: 'Example project 3',
                                scans: [
                                    {
                                        id: 'cc1d2f01-7d20-42b1-947f-08f646271f9e',
                                        name: 'P3_Scan_001',
                                        captureDate: '2025-04-03T16:25:31+02:00',
                                        filesPath: 'Scans/P3_Scan_001.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                    {
                                        id: 'f5d1011f-18f1-4e3c-8d55-0179b9913a1e',
                                        name: 'P3_Scan_002',
                                        captureDate: '2025-04-03T16:35:31+02:00',
                                        filesPath: 'Scans/P3_Scan_002.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                    {
                                        id: '1d40549f-ff34-45e3-90f2-2d54c81aa498',
                                        name: 'P3_Scan_003',
                                        captureDate: '2025-04-03T16:45:31+02:00',
                                        filesPath: 'Scans/P3_Scan_003.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                ],
                            },
                            {
                                id: 'd7b3676e-3b90-4d6c-9086-71be2c7f5b49',
                                name: 'Example project 4',
                                scans: [
                                    {
                                        id: '07b2f859-3e5c-4c93-9185-3d69a45281a9',
                                        name: 'P4_Scan_001',
                                        captureDate: '2025-04-03T15:22:31+02:00',
                                        filesPath: 'Scans/P4_Scan_001.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                    {
                                        id: '35b32b83-d2c6-4631-8c82-cd1f6c648390',
                                        name: 'P4_Scan_002',
                                        captureDate: '2025-04-03T15:37:31+02:00',
                                        filesPath: 'Scans/P4_Scan_002.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                    {
                                        id: '9e206c99-1af3-4fc0-8f29-f370bd5053ee',
                                        name: 'P4_Scan_003',
                                        captureDate: '2025-04-03T15:55:31+02:00',
                                        filesPath: 'Scans/P4_Scan_003.fls',
                                        capturedWithCamera: true,
                                        scannerType: 'CR-P1 L',
                                    },
                                ],
                            },
                        ],
                    },
                    path: globalData?.filePath ?? 'D:\\',
                    size: globalData?.fileSize ?? '200B',
                    success: globalData?.success !== undefined ? globalData.success : true,
                },
            }),
        },
    ],
};

const getFileContentsCloseDialog: ProcessData = {
    name: 'getFileContentsCloseDialog',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            event: (params, globalData) => ({
                type: 'ACTION GET_FILE_CONTENTS RESPONSE',
                message: {
                    success: false,
                },
            }),
        },
    ],
};

const getFileContentsPathError: ProcessData = {
    name: 'getFileContentsPathError',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params, globalData) => ({
                type: 'ACTION GET_FILE_CONTENTS RESPONSE',
                message: {
                    path: globalData?.filePath || 'D:\\',
                    success: false,
                    error: 'INVALID_DATA_PATH',
                },
            }),
        },
    ],
};

const getTransferStationDataQueuedResponse: ProcessData = {
    name: 'getTransferStationDataQueuedResponse',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params, globalData) => ({
                type: 'GET TRANSFER_STATION_DATA_QUEUED RESPONSE',
                message: {
                    stationUuid: globalData?.stationUuid ?? '0600_0000_0000aaaa-0000-aaaa-0000-aaaa0000aaaa',
                },
            }),
        },
    ],
};

const getCopyCompressFilesResponse: ProcessData = {
    name: 'getCopyCompressFilesResponse',
    steps: [
        {
            action: STEP_ACTION_TYPE.SEND,
            runDelay: 1000,
            event: (params, globalData) => ({
                type: 'ACTION COPY_AND_COMPRESS_FILES RESPONSE',
                message: {
                    success: globalData?.success === undefined ? true : globalData?.success,
                },
            }),
        },
    ],
};

/* Include here every process to load it */
export const processMocks: ProcessData[] = [
    instrumentHeight,
    stationTransformations,
    stationPosition,
    requestUserPreferences,
    requestBounding,
    requestPivotPoint,
    requestProjectName,
    notifyWorkflowState,
    waitForNotifyWorkflowState,
    notifyScannerState,
    createNewStation,
    sendScanTarget,
    sendScanTarget2,
    waitForTransferStationRequest,
    setCurrentStation,
    getScanCloud,
    getWindowScanCloud,
    getScanTarget,
    getScanTarget2,
    getTransferStation,
    getScannerState,
    waitForLayoutPointRequest,
    getStandardVerification,
    getTotalStationVerification,
    notifyScannerStateFail,
    instrumentHeightFail,
    sendScanTargetFail,
    setCurrentStationFail,
    createNewStationFail,
    getScanCloudFail,
    getTransferStationFail,
    requestProjectData,
    createNewStationManually,
    startReportPickPoint,
    stopReportPickPoint,
    waitForNotifyNavigationView,
    setPrismPolePosition,
    waitForTotalStationVerification,
    getScanCloudAndResection,
    getMultiScanCloud,
    waitForColumnSize,
    getFileContents,
    getFileContentsCloseDialog,
    getFileContentsPathError,
    getTransferStationDataQueuedResponse,
    getCopyCompressFilesResponse,
];
